import Button from 'tailwind-components/Button/Button';
import Card from 'components/Card/Card';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import propTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button as MuiLinkButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  getDefaultSupportContacts,
  t
} from 'constants/reconciliationFormsI18n';
import endpoints from 'constants/endpoints';
import { safeAWSCallGetBlob } from 'helpers/firebaseSetup';
import {
  buildCarrierMailto,
  getEffectiveCcList,
  RECONCILIATION_FORMS_ALWAYS_CC
} from 'helpers/reconciliationFormsMailto';
import { showAlert } from 'state/actions/actions';
import style from './reconciliationForms.module.scss';

/** Email template samples are always shown in English (carrier-facing copy). */
const EMAIL_TEMPLATE_LANG = 'en';

function sortRecords(list) {
  return [...list].sort((a, b) =>
    String(a.carrierName).localeCompare(String(b.carrierName))
  );
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

function ReconciliationFormsView({
  records = [],
  contacts = null,
  getReconciliationForms,
  setLocalLoader
}) {
  const dispatch = useDispatch();
  const [lang, setLang] = useState('es');
  const [search, setSearch] = useState('');
  const [tplCopied, setTplCopied] = useState({ 1: false, 2: false });
  const [downloadingTemplateId, setDownloadingTemplateId] = useState(null);

  const cancelTokenSource = useMemo(() => axios.CancelToken.source(), []);

  useEffect(() => {
    getReconciliationForms(setLocalLoader, cancelTokenSource);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  const notify = (message, code = 'success') => {
    dispatch(showAlert({ message, code }));
  };

  const activeRecords = useMemo(
    () => (records || []).filter(r => r.active),
    [records]
  );

  const sorted = useMemo(() => sortRecords(activeRecords), [activeRecords]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sorted;
    return sorted.filter(r => {
      const blob = [
        r.carrierName,
        r.region,
        r.sendToEmail,
        ...getEffectiveCcList(r)
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(term);
    });
  }, [sorted, search]);

  const templateCount = useMemo(
    () => sorted.filter(r => r.hasTemplateFile || r.requiresTemplate).length,
    [sorted]
  );

  const supportContacts = useMemo(
    () => (contacts?.length ? contacts : getDefaultSupportContacts(lang)),
    [contacts, lang]
  );

  const copyTpl = async (n, body) => {
    const ok = await copyText(body);
    if (ok) {
      setTplCopied(s => ({ ...s, [n]: true }));
      notify(t(lang, 'toast.copiedTpl'));
      setTimeout(() => setTplCopied(s => ({ ...s, [n]: false })), 1600);
    } else notify(t(lang, 'toast.copyFail'), 'error');
  };

  const copyEmail = async email => {
    const ok = await copyText(email);
    if (ok) notify(t(lang, 'toast.copied'));
    else notify(t(lang, 'toast.copyFail'), 'error');
  };

  const onDownloadClick = () => {
    notify(t(lang, 'toast.downloading'), 'info');
  };

  const getDownloadFileName = (headers = {}, row = {}) => {
    const disposition =
      headers['content-disposition'] || headers['Content-Disposition'] || '';
    const utf8NameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8NameMatch?.[1]) return decodeURIComponent(utf8NameMatch[1]);
    const plainNameMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (plainNameMatch?.[1]) return plainNameMatch[1];
    if (row.templateFileName) return row.templateFileName;

    const contentType =
      headers['content-type'] || headers['Content-Type'] || '';
    const base = row.carrierName
      ? `${String(row.carrierName).replace(
          /[/\\?%*:|"<>]/g,
          '-'
        )}-Reconciliation`
      : 'reconciliation-template';
    if (/application\/zip|\/(?:x-)?zip\b/i.test(contentType))
      return `${base}.zip`;
    return `${base}.xlsx`;
  };

  const downloadTemplateFromEndpoint = async row => {
    if (!row?.id) return;
    const routeBase =
      endpoints?.aws?.reconciliationTemplate || '/reconciliation';
    const route = `${routeBase}/${encodeURIComponent(row.id)}`;
    setDownloadingTemplateId(row.id);
    notify(t(lang, 'toast.downloading'), 'info');

    try {
      const response = await safeAWSCallGetBlob(route, {}, false);
      if (!response?.data) throw new Error('No template payload');

      const fileName = getDownloadFileName(response.headers || {}, row);
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      notify(
        lang === 'es'
          ? 'No se pudo descargar la plantilla'
          : 'Failed to download template',
        'error'
      );
    } finally {
      setDownloadingTemplateId(null);
    }
  };

  return (
    <div className={style.page}>
      <div className={style.stack}>
        <Card
          title={t(lang, 'hero.title')}
          titleOptionsSide={
            <ToggleButtonGroup
              exclusive
              size='small'
              value={lang}
              onChange={(_, v) => v && setLang(v)}
              aria-label='Language'
            >
              <ToggleButton value='es'>ES</ToggleButton>
              <ToggleButton value='en'>EN</ToggleButton>
            </ToggleButtonGroup>
          }
        >
          <p className={style.lede}>{t(lang, 'hero.sub')}</p>
          <div className={style.statsRow}>
            <div className={style.stat}>
              <span className={style.statNum}>{sorted.length}</span>
              <span className={style.statLabel}>{t(lang, 'hero.stat1')}</span>
            </div>
            <div className={style.stat}>
              <span className={style.statNum}>{templateCount}</span>
              <span className={style.statLabel}>{t(lang, 'hero.stat2')}</span>
            </div>
          </div>
          <p className={style.note}>{t(lang, 'hero.cardNote')}</p>
        </Card>

        <div id='recon-instructions'>
          <Card title={t(lang, 'instructions.title')}>
            <p className={style.lede}>{t(lang, 'instructions.lede')}</p>
            <div className={style.stepsGrid}>
              {[1, 2, 3, 4].map(n => (
                <div key={n} className={style.step}>
                  <div className={style.stepNum}>{n}</div>
                  <h3>{t(lang, `instructions.step${n}.title`)}</h3>
                  <p>{t(lang, `instructions.step${n}.body`)}</p>
                </div>
              ))}
            </div>
            <div className={style.alert}>
              <div className={style.alertIcon}>!</div>
              <div className={style.alertBody}>
                <strong>{t(lang, 'alert.important')}</strong>
                <p>{t(lang, 'alert.importantBody')}</p>
              </div>
            </div>
          </Card>
        </div>

        <div id='recon-carriers'>
          <Card title={t(lang, 'carriers.title')}>
            <p className={style.lede}>{t(lang, 'carriers.lede')}</p>

            {sorted.length === 0 ? (
              <p className={style.empty}>
                {lang === 'es'
                  ? 'No hay aseguradoras activas en Zoho o no se pudo cargar la información.'
                  : 'No active carriers in Zoho, or data could not be loaded.'}
              </p>
            ) : (
              <>
                <div className={style.toolbar}>
                  <TextField
                    className={style.searchField}
                    size='small'
                    type='search'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t(lang, 'carriers.searchPh')}
                    variant='outlined'
                    inputProps={{ 'aria-label': t(lang, 'carriers.searchPh') }}
                  />
                  <span className={style.count}>
                    {filtered.length} {t(lang, 'carriers.of')} {sorted.length}
                  </span>
                </div>

                <div className={style.carrierGrid}>
                  {filtered.map(row => {
                    const accent = row.carrierColor || '#3C6EB2';
                    const mailto = buildCarrierMailto(row, lang);
                    const tagExternal = row.tagType !== 'internal';
                    const canDownloadTemplate =
                      row.requiresTemplate || row.hasTemplateFile;
                    const note = row.isOther
                      ? t(lang, 'carrier.otherNote')
                      : !canDownloadTemplate
                      ? t(lang, 'carrier.emailOnlyNote')
                      : null;
                    const ccList = getEffectiveCcList(row);

                    const downloadArea = canDownloadTemplate ? (
                      row.requiresTemplate ? (
                        <button
                          type='button'
                          className={`${style.downloadBlock} ${style.downloadBlockButton}`}
                          style={{ '--accent': accent }}
                          title='Download template'
                          onClick={() => downloadTemplateFromEndpoint(row)}
                          disabled={downloadingTemplateId === row.id}
                        >
                          <span className={style.mono}>{row.monogram}</span>
                          <span>
                            {downloadingTemplateId === row.id
                              ? lang === 'es'
                                ? 'Descargando...'
                                : 'Downloading...'
                              : 'Download template'}
                          </span>
                        </button>
                      ) : (
                        <a
                          className={style.downloadBlock}
                          href={row.templateUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          download={row.templateFileName || undefined}
                          style={{ '--accent': accent }}
                          title={t(lang, 'carrier.download')}
                          onClick={onDownloadClick}
                        >
                          <span className={style.mono}>{row.monogram}</span>
                          <span>{t(lang, 'carrier.download')}</span>
                        </a>
                      )
                    ) : (
                      <div
                        className={`${style.downloadBlock} ${style.downloadBlockStatic}`}
                        style={{ '--accent': accent }}
                      >
                        <span className={style.mono}>{row.monogram}</span>
                        <span style={{ fontSize: 11 }}>
                          {row.isOther
                            ? t(lang, 'carrier.tagInternal')
                            : lang === 'es'
                            ? 'Solo correo'
                            : 'Email only'}
                        </span>
                      </div>
                    );

                    return (
                      <article
                        key={row.id || row.carrierName}
                        className={style.carrierCard}
                        style={{ '--accent': accent }}
                      >
                        <div className={style.carrierHead}>
                          <div className={style.carrierNameBlock}>
                            <h3 className={style.carrierName}>
                              {row.carrierName}
                            </h3>
                            {row.region ? (
                              <span className={style.carrierRegion}>
                                {row.region}
                              </span>
                            ) : null}
                          </div>
                          <span
                            className={`${style.tag} ${
                              tagExternal ? style.tagExternal : ''
                            }`}
                          >
                            {tagExternal
                              ? t(lang, 'carrier.tagExternal')
                              : t(lang, 'carrier.tagInternal')}
                          </span>
                        </div>

                        {downloadArea}

                        <div className={style.emailBlock}>
                          <div className={style.emailGroup}>
                            <div className={style.metaLabel}>
                              {t(lang, 'carrier.email')}
                            </div>
                            <div className={style.emails}>
                              <a href={`mailto:${row.sendToEmail}`}>
                                {row.sendToEmail}
                              </a>
                            </div>
                          </div>
                          {ccList.length > 0 ? (
                            <div className={style.emailGroup}>
                              <div className={style.metaLabel}>
                                {t(lang, 'carrier.cc')}
                              </div>
                              <div className={style.emails}>
                                {ccList.map(ccAddr => (
                                  <a key={ccAddr} href={`mailto:${ccAddr}`}>
                                    {ccAddr}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        {note ? (
                          <p className={style.carrierNote}>{note}</p>
                        ) : null}

                        <div className={style.actions}>
                          {mailto ? (
                            <MuiLinkButton
                              variant='contained'
                              size='small'
                              href={mailto}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              {t(lang, 'carrier.submit')}
                            </MuiLinkButton>
                          ) : (
                            <Button
                              variant='default'
                              size='small'
                              rounded='small'
                              text={t(lang, 'carrier.submit')}
                              isDisabled
                            />
                          )}
                          <Button
                            variant='default'
                            size='small'
                            rounded='small'
                            outlined
                            text={t(lang, 'carrier.copyEmail')}
                            onClick={() => copyEmail(row.sendToEmail)}
                          />
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        </div>

        <div id='recon-email'>
          <Card title={t(lang, 'email.title')}>
            <p className={style.lede}>{t(lang, 'email.lede')}</p>
            <div className={style.emailGrid}>
              <div className={style.emailPanel}>
                <div className={style.emailPanelHead}>
                  <h3>{t(lang, 'email.tpl1.title')}</h3>
                  <Button
                    variant='default'
                    size='xsmall'
                    rounded='small'
                    outlined
                    text={
                      tplCopied[1]
                        ? t(lang, 'email.copied')
                        : t(lang, 'email.copy')
                    }
                    onClick={() =>
                      copyTpl(1, t(EMAIL_TEMPLATE_LANG, 'email.tpl1.body'))
                    }
                  />
                </div>
                <div className={style.emailFields}>
                  <div className={style.emailField}>
                    <span className={style.emailFieldLabelBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.to')}:
                    </span>
                    <span className={style.emailFieldValueBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.tpl1.to')}
                    </span>
                  </div>
                  <div className={style.emailField}>
                    <span className={style.emailFieldLabelBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.cc')}:
                    </span>
                    <span className={style.emailFieldValueBlock}>
                      {RECONCILIATION_FORMS_ALWAYS_CC.join(', ')}
                    </span>
                  </div>
                  <div className={style.emailField}>
                    <span className={style.emailFieldLabelBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.subject')}:
                    </span>
                    <span className={style.emailFieldValueBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.tpl1.subject')}
                    </span>
                  </div>
                </div>
                <pre className={style.emailBody}>
                  {t(EMAIL_TEMPLATE_LANG, 'email.tpl1.body')}
                </pre>
              </div>

              <div className={style.emailPanel}>
                <div className={style.emailPanelHead}>
                  <h3>{t(lang, 'email.tpl2.title')}</h3>
                  <Button
                    variant='default'
                    size='xsmall'
                    rounded='small'
                    outlined
                    text={
                      tplCopied[2]
                        ? t(lang, 'email.copied')
                        : t(lang, 'email.copy')
                    }
                    onClick={() =>
                      copyTpl(2, t(EMAIL_TEMPLATE_LANG, 'email.tpl2.body'))
                    }
                  />
                </div>
                <div className={style.emailFields}>
                  <div className={style.emailField}>
                    <span className={style.emailFieldLabelBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.to')}:
                    </span>
                    <span className={style.emailFieldValueBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.tpl2.to')}
                    </span>
                  </div>
                  <div className={style.emailField}>
                    <span className={style.emailFieldLabelBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.cc')}:
                    </span>
                    <span className={style.emailFieldValueBlock}>
                      {RECONCILIATION_FORMS_ALWAYS_CC.join(', ')}
                    </span>
                  </div>
                  <div className={style.emailField}>
                    <span className={style.emailFieldLabelBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.subject')}:
                    </span>
                    <span className={style.emailFieldValueBlock}>
                      {t(EMAIL_TEMPLATE_LANG, 'email.tpl2.subject')}
                    </span>
                  </div>
                </div>
                <pre className={style.emailBody}>
                  {t(EMAIL_TEMPLATE_LANG, 'email.tpl2.body')}
                </pre>
              </div>
            </div>
          </Card>
        </div>

        <div id='recon-contact'>
          <Card title={t(lang, 'contact.title')}>
            <p className={style.contactIntro}>
              <span className={`${style.metaLabel} ${style.eyebrowBlock}`}>
                {t(lang, 'contact.eyebrow')}
              </span>
              {t(lang, 'contact.lede')}
            </p>
            <div className={style.contactList}>
              {supportContacts.map((c, i) =>
                c.type === 'phone' ? (
                  <a
                    key={`p-${i}`}
                    className={style.contactLink}
                    href={`tel:${String(c.tel).replace(/\s/g, '')}`}
                  >
                    <span className={style.contactRole}>{c.name}</span>
                    <span className={style.contactDetail}>{c.display}</span>
                  </a>
                ) : (
                  <a
                    key={`e-${i}`}
                    className={style.contactLink}
                    href={`mailto:${c.email}`}
                  >
                    <span className={style.contactRole}>{c.name}</span>
                    <span className={style.contactDetail}>{c.email}</span>
                  </a>
                )
              )}
            </div>
            <Box sx={{ mt: 2, fontSize: 12, color: 'text.secondary' }}>
              {t(lang, 'footer.copy')} · {t(lang, 'footer.internal')}
            </Box>
          </Card>
        </div>
      </div>
    </div>
  );
}

ReconciliationFormsView.propTypes = {
  records: propTypes.arrayOf(propTypes.object),
  contacts: propTypes.arrayOf(propTypes.object),
  getReconciliationForms: propTypes.func.isRequired,
  setLocalLoader: propTypes.func.isRequired
};

export default ReconciliationFormsView;
